import {
  getDayType,
  isGlobalInactive,
  isLineDisrupted,
  hasServiceToday,
  escapeHtml,
  minsToHHMM,
  getScheduleKey,
  resolveDirection,
  getAppDate
} from "./utils.js";

import { TRAIN_STATIONS } from "../data/trains.js";
import { getStopName, STOP_NAMES } from "./line-config.js";
import { patchDOM } from "./dom-utils.js";
import { renderStrikeBanner } from "./alerts.js";
import { fetchGtfsStatus } from "./gtfs-status.js";
import {
  FILTER_CITY_ORDER,
  compareCardsByDeparture,
  buildOutboundCard,
  buildReturnCard
} from "./live-logic";
import {
  renderFeaturedCard,
  renderLineCard,
  renderTrainCard,
  renderRecentlyDepartedBlock
} from "./live-cards.js";
import { bindLiveEvents } from "./live-events.js";
import { startCountdown } from "./countdown";
import { buildHeroViewModel } from "./hero-viewmodel";
import { renderNowBanner } from "./now-banner";

let lastArgs = null;
let _gtfsStatus = null;
let stopLiveCountdown = null;

export function stopLiveTicker() {
  if (stopLiveCountdown) {
    stopLiveCountdown();
    stopLiveCountdown = null;
  }
}

// Kick off GTFS status fetch (non-blocking)
fetchGtfsStatus()
  .then((status) => {
    _gtfsStatus = status;
  })
  .catch(() => {});

/**
 * Render a GTFS feed expiry banner if the feed is expiring or expired.
 */
function renderGtfsExpiryBanner() {
  if (!_gtfsStatus || _gtfsStatus.state === "ok") return "";
  if (_gtfsStatus.state === "expired") {
    return `<div class="banner banner-danger gtfs-expiry-banner" style="margin-bottom: 10px; font-size: 0.8rem; padding: 8px 12px;">
      <strong>⚠️ Orari scaduti</strong> — I dati GTFS sono scaduti il ${_gtfsStatus.to || "?"}.
    </div>`;
  }
  if (_gtfsStatus.daysLeft <= 2) {
    return `<div class="banner banner-warning gtfs-expiry-banner" style="margin-bottom: 10px; font-size: 0.8rem; padding: 8px 12px;">
      <strong>⏳ Orari in scadenza</strong> — I dati GTFS scadono tra ${_gtfsStatus.daysLeft} giorn${_gtfsStatus.daysLeft === 1 ? "o" : "i"}.
    </div>`;
  }
  return "";
}

export function renderLive(state, lineData, lineConfig, cfg, saveSettings) {
  lastArgs = { state, lineData, lineConfig, cfg, saveSettings };
  const container = document.getElementById("live-content");
  if (!container) return;

  const now = getAppDate();
  const currentMin = now.getHours() * 60 + now.getMinutes();
  const dayType = getDayType(now, cfg);
  const baseDirection = state.settings.liveDirection || cfg.defaults.liveDirection || "outbound";
  const direction = resolveDirection(baseDirection, state.settings);
  const allLines = Object.keys(lineConfig);
  const cityLines = cfg.lineOrder || allLines;
  const showAllLines = !!state.showAllLines;
  // If user toggled "show all lines", use full set; otherwise use city-specific
  // and apply userProfile.activeLines filter if onboarding chose specific lines.
  const userActiveLines = state.settings?.userProfile?.activeLines;
  const focusCity = state.settings?.focusCity || cfg.defaults?.focusCity || "BT";
  const followedForCity = state.settings?.followedLinesByCity?.[focusCity];

  let lineOrder;
  if (showAllLines) {
    lineOrder = allLines;
  } else if (Array.isArray(followedForCity)) {
    lineOrder = followedForCity.filter((id) => lineConfig[id]);
  } else if (
    Array.isArray(userActiveLines) &&
    userActiveLines.length > 0 &&
    !state.settings?.userProfile?.skipped &&
    focusCity === (state.settings?.userProfile?.focusCity || "BT")
  ) {
    lineOrder = cityLines.filter((id) => userActiveLines.includes(id));
  } else {
    lineOrder = cityLines;
  }

  const stopFilter = state.liveStopFilter || null;
  const lineFilter = state.liveLineFilter || null;

  // Dynamic title based on focus city and inversion preference
  const cityName = cfg.activeCityConfig?.name || cfg.homeProfile?.address || "Busto Garolfo";
  const title =
    baseDirection === "return"
      ? state.settings?.invertDirections
        ? `Ritorno da ${cityName}`
        : `Ritorno verso ${cityName}`
      : state.settings?.invertDirections
        ? `Andata verso ${cityName}`
        : `Andata da ${cityName}`;
  const subtitle =
    baseDirection === "return"
      ? "Interscambi e fermate di ritorno verso casa."
      : "Fermate preferite con fallback automatico per ogni linea.";

  let html = `
    <section class="hero-panel">
      <div style="flex: 1; min-width: 0;">
        <p class="section-eyebrow">${escapeHtml(cfg.homeProfile.address)}</p>
        <h2 style="display: flex; align-items: center; gap: 8px; flex-wrap: wrap;">
          ${title}
          <button type="button" class="live-customize-trigger" title="Personalizza scheda LIVE" aria-label="Personalizza scheda LIVE">
            <svg viewBox="0 0 24 24" width="18" height="18" stroke="currentColor" stroke-width="2.5" fill="none" stroke-linecap="round" stroke-linejoin="round" style="vertical-align: middle;"><circle cx="12" cy="12" r="3"></circle><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"></path></svg>
          </button>
        </h2>
        <p>${escapeHtml(subtitle)}</p>
      </div>
      <div class="segmented" data-live-direction>
        <button type="button" data-dir="outbound" class="${baseDirection === "outbound" ? "active" : ""}">Andata</button>
        <button type="button" data-dir="return" class="${baseDirection === "return" ? "active" : ""}">Ritorno</button>
      </div>
    </section>`;

  const followedLineOrder = Array.isArray(followedForCity)
    ? followedForCity.filter((id) => lineConfig[id])
    : Array.isArray(userActiveLines) &&
        userActiveLines.length > 0 &&
        !state.settings?.userProfile?.skipped &&
        focusCity === (state.settings?.userProfile?.focusCity || "BT")
      ? cityLines.filter((id) => userActiveLines.includes(id))
      : cityLines;

  html += renderFilterBar(lineConfig, cfg, stopFilter, lineFilter, showAllLines, followedLineOrder);

  // Strike banner (only active strikes, not general notices)
  html += renderStrikeBanner();

  // GTFS feed expiry banner
  html += renderGtfsExpiryBanner();

  if (isGlobalInactive(now, cfg)) {
    html += `<div class="banner banner-danger">Servizio sospeso: ${escapeHtml(cfg.globalInactivity.note)}</div>`;
  }

  const cards = [];
  for (const lineId of lineOrder) {
    if (!lineConfig[lineId]?.showInLive) continue;
    if (lineFilter && lineFilter !== lineId) continue;
    const hasService = hasServiceToday(lineId, dayType, lineConfig, lineData, now, cfg);
    const disrupted = isLineDisrupted(lineId, now, cfg);
    if (!hasService && !disrupted) continue;
    try {
      const card =
        direction === "return"
          ? buildReturnCard(
              lineId,
              state,
              lineData,
              lineConfig,
              cfg,
              currentMin,
              dayType,
              disrupted,
              stopFilter
            )
          : buildOutboundCard(
              lineId,
              state,
              lineData,
              lineConfig,
              cfg,
              currentMin,
              dayType,
              disrupted,
              stopFilter
            );
      if (card) {
        if (stopFilter && !card.hasTrips) continue;
        cards.push(card);
      }
    } catch (cardError) {
      console.error(`[Trasporti] Errore nel calcolo della card ${lineId}:`, cardError);
    }
  }

  if (cards.length === 0) {
    const msg =
      stopFilter || lineFilter
        ? "Nessuna corsa trovata con i filtri attivi."
        : "Nessuna corsa utile trovata per questa modalità.";

    let suggestionHtml = "";
    if (!stopFilter && !lineFilter) {
      // Find the soonest trip in the next 3 hours (180 mins)
      let soonestTrip = null;
      let soonestLineId = "";
      let soonestWait = Infinity;
      let soonestStop = "";

      for (const lId of Object.keys(lineConfig)) {
        const scheduleKey = getScheduleKey(lId, dayType, direction);
        const trips = lineData[lId]?.[scheduleKey] || [];
        for (const trip of trips) {
          const depMin = trip._depMin ?? trip.departureMin;
          if (depMin !== undefined && depMin !== null) {
            const wait = depMin - currentMin;
            if (wait > 0 && wait <= 180 && wait < soonestWait) {
              soonestWait = wait;
              soonestTrip = trip;
              soonestLineId = lId;
              soonestStop = trip._depStop || Object.keys(trip.stops || {})[0] || "";
            }
          }
        }
      }

      if (soonestTrip) {
        const timeStr = minsToHHMM(soonestTrip._depMin ?? soonestTrip.departureMin);
        const stopName = soonestStop ? getStopName(soonestStop) : "fermata di partenza";
        suggestionHtml = `
          <div class="alert-card alert-card--notice" style="margin-top: 14px; text-align: left; padding: 12px; border: 1px solid var(--accent); background: rgba(34, 211, 238, 0.05); border-radius: 8px;">
            <div style="font-weight: 700; color: var(--accent); font-size: 0.85rem; margin-bottom: 4px; display: flex; align-items: center; gap: 6px;">
              💡 Corsa imminente trovata
            </div>
            <p style="font-size: 0.78rem; color: var(--muted); margin: 0;">
              La prima corsa utile è sulla linea <strong>${soonestLineId}</strong> in partenza alle <strong>${timeStr}</strong> (tra ${soonestWait} min) da <strong>${escapeHtml(stopName)}</strong>.
            </p>
          </div>
        `;
      }
    }

    html += `<div class="empty-state">
      <strong>${msg}</strong>
      ${suggestionHtml}
    </div>`;
  } else {
    cards.sort(compareCardsByDeparture);
    if (!stopFilter) {
      const heroCode = state.settings.liveHero || "Z649";
      if (!heroCode.endsWith("_FS")) {
        const hero = cards.find((card) => card.lineId === heroCode && card.hasTrips);
        if (hero) {
          try {
            if (direction === "outbound") {
              const heroVm = buildHeroViewModel(hero, direction, cfg, currentMin, state);
              html += renderNowBanner(heroVm, currentMin, hero, cfg, state);
            }
            html += renderFeaturedCard(hero, direction, cfg, currentMin, state);
          } catch (e) {
            console.error("[Trasporti] Errore nel render della featured card:", e);
          }
        }
      } else {
        try {
          html += renderTrainCard(heroCode, state, currentMin, cfg, true);
        } catch (e) {
          console.error("[Trasporti] Errore nel render del treno Hero:", e);
        }
      }
    }

    if (stopFilter) {
      html += `<div class="section-title">Partenze da ${escapeHtml(getStopName(stopFilter))}</div>`;
      html += cards
        .map((card) => {
          try {
            return renderLineCard(card, direction, cfg, currentMin, state);
          } catch (e) {
            console.error(`[Trasporti] Errore nel render card ${card.lineId}:`, e);
            return `<div class="empty-mini" style="border-color: rgba(239,68,68,0.3); color: #fecaca;">Errore nel caricamento di ${card.lineId}</div>`;
          }
        })
        .join("");
    } else {
      // Progressive Disclosure (LIVE-UX):
      // Section 1: "Prossime partenze" (top 3 upcoming departures, zero scroll needed)
      // Section 2: "Altre linee attive" (collapsed by default, tap to expand)
      const upcomingCards = cards.filter((c) => c.hasTrips);
      const remainingCards = cards.filter((c) => !c.hasTrips);

      const topDepartures = upcomingCards.slice(0, 3);
      const otherUpcoming = upcomingCards.slice(3);
      const otherActiveCards = [...otherUpcoming, ...remainingCards];

      if (topDepartures.length > 0) {
        html += `<div class="section-title">🕒 Prossime partenze</div>`;
        html += topDepartures
          .map((card) => {
            try {
              return renderLineCard(card, direction, cfg, currentMin, state);
            } catch (e) {
              console.error(`[Trasporti] Errore nel render card ${card.lineId}:`, e);
              return `<div class="empty-mini" style="border-color: rgba(239,68,68,0.3); color: #fecaca;">Errore nel caricamento di ${card.lineId}</div>`;
            }
          })
          .join("");

        if (otherActiveCards.length > 0) {
          html += `
            <details class="live-other-lines-details" ${lineFilter ? "open" : ""}>
              <summary>
                <span>🚌 Altre linee attive (${otherActiveCards.length})</span>
                <span class="live-other-lines-toggle-hint">Tocca per mostrare ▾</span>
              </summary>
              <div class="live-other-lines-body">
                ${otherActiveCards
                  .map((card) => {
                    try {
                      return renderLineCard(card, direction, cfg, currentMin, state);
                    } catch (e) {
                      console.error(`[Trasporti] Errore nel render card ${card.lineId}:`, e);
                      return `<div class="empty-mini" style="border-color: rgba(239,68,68,0.3); color: #fecaca;">Errore nel caricamento di ${card.lineId}</div>`;
                    }
                  })
                  .join("")}
              </div>
            </details>
          `;
        }
      } else {
        html += `<div class="section-title">🚌 Linee attive</div>`;
        html += otherActiveCards
          .map((card) => {
            try {
              return renderLineCard(card, direction, cfg, currentMin, state);
            } catch (e) {
              console.error(`[Trasporti] Errore nel render card ${card.lineId}:`, e);
              return `<div class="empty-mini" style="border-color: rgba(239,68,68,0.3); color: #fecaca;">Errore nel caricamento di ${card.lineId}</div>`;
            }
          })
          .join("");
      }
    }
  }

  if (!stopFilter) {
    try {
      html += renderRecentlyDepartedBlock(
        state,
        lineData,
        lineConfig,
        cfg,
        currentMin,
        dayType,
        direction
      );
    } catch (e) {
      console.error("[Trasporti] Errore nel render recenti:", e);
    }
  }
  if (!stopFilter) {
    const heroCode = state.settings.liveHero || "Z649";
    const visibleTrains = state.settings.visibleTrains || ["CN_FS"];
    for (const stationCode of visibleTrains) {
      if (stationCode === heroCode) continue; // skip if rendered as Hero!
      try {
        html += renderTrainCard(stationCode, state, currentMin, cfg, false);
      } catch (e) {
        console.error(`[Trasporti] Errore nel render del treno ${stationCode}:`, e);
      }
    }
  }

  html += `<div class="app-footer">Dati aggiornati al ${escapeHtml(cfg.lastUpdate)}. Coincidenze treno/metro stimate.</div>`;

  // Append the slide-up modal HTML
  const heroCode = state.settings.liveHero || "Z649";
  const visibleTrains = state.settings.visibleTrains || ["CN_FS"];

  // Options for the Home Hero dropdown select
  const heroOptions = [
    ...lineOrder.map(
      (id) =>
        `<option value="${id}" ${heroCode === id ? "selected" : ""}>Bus ${id} - ${escapeHtml(lineConfig[id]?.destination || id)}</option>`
    ),
    ...Object.entries(TRAIN_STATIONS).map(
      ([code, info]) =>
        `<option value="${code}" ${heroCode === code ? "selected" : ""}>Treno - Stazione di ${escapeHtml(info.name)}</option>`
    )
  ].join("");

  html += `
    <div id="live-customize-modal" class="live-customize-modal">
      <div class="live-customize-sheet">
        <div class="live-customize-header">
          <h3>Scheda Principale (HERO)</h3>
          <button type="button" class="live-customize-close" aria-label="Chiudi">&times;</button>
        </div>
        <div class="live-customize-body">
          <div class="live-customize-section">
            <label class="section-label">Scheda in evidenza in alto</label>
            <p style="font-size: 0.75rem; color: var(--muted); margin: 0 0 8px 0;">Scegli quale linea o stazione mostrare per prima con conto alla rovescia in evidenza.</p>
            <select id="customize-live-hero" style="width:100%; background: var(--input-bg, rgba(255,255,255,0.05)); border: 1px solid var(--line); border-radius: 8px; color: var(--text); padding: 8px 12px; font-size: 0.9rem;">
              ${heroOptions}
            </select>
          </div>
          <div style="background: rgba(255,255,255,0.02); border: 1px solid var(--line); border-radius: 8px; padding: 12px; margin-top: 10px;">
            <p style="margin: 0; font-size: 0.78rem; color: var(--muted); line-height: 1.4;">
              ⚙️ Per gestire le linee visibili, le stazioni ferroviarie e l'inversione delle direzioni, vai nella sezione <strong>Impostazioni</strong>.
            </p>
          </div>
        </div>
        <div class="live-customize-footer">
          <button type="button" class="btn btn-secondary live-customize-close-btn" style="flex: 1; padding: 10px; border-radius: 8px; font-weight: 600; cursor: pointer;">Annulla</button>
          <button type="button" class="btn btn-primary" id="save-live-customization" style="flex: 1; padding: 10px; border-radius: 8px; font-weight: 600; cursor: pointer; background: var(--accent); color: #000; border: none;">Salva</button>
        </div>
      </div>
    </div>
  `;

  // Capture any dep-stop-select that is currently in edit mode (visible).
  // patchDOM preserves the SELECT's style (via dom-utils fix), but resets the
  // sibling .dep-stop-name-text and .edit-dep-stop-btn back to visible.
  // We save the state here and restore it in onAfterPatch to avoid duplication.
  const activeEdits = [];
  container.querySelectorAll(".dep-stop-select").forEach((sel) => {
    if (sel.style.display && sel.style.display !== "none") {
      activeEdits.push({
        line: sel.dataset.line,
        dir: sel.dataset.dir,
        shownAt: sel.__shown_at || Date.now()
      });
    }
  });

  patchDOM(container, html, {
    onAfterPatch: () => {
      if (stopLiveCountdown) {
        stopLiveCountdown();
      }
      stopLiveCountdown = startCountdown(container);

      // Restore active edit states: hide text+btn, keep select visible
      activeEdits.forEach(({ line, dir, shownAt }) => {
        const sel = container.querySelector(
          `.dep-stop-select[data-line="${line}"][data-dir="${dir}"]`
        );
        if (sel) {
          const wrapper = sel.closest(".dep-stop-edit-wrapper");
          const staticText = wrapper?.querySelector(".dep-stop-name-text");
          const editBtn = wrapper?.querySelector(".edit-dep-stop-btn");
          if (staticText) staticText.style.visibility = "hidden";
          if (editBtn) editBtn.style.visibility = "hidden";
          sel.style.display = "inline-block";
          sel.__shown_at = shownAt;
        }
      });
      bindLiveEvents(container, {
        getArgs: () => lastArgs,
        renderLive,
        buildSearchResults,
        getSearchHistoryChipsHTML
      });
    }
  });
}

function getSearchHistoryChipsHTML() {
  try {
    const history = JSON.parse(localStorage.getItem("trasporti_search_history") || "[]");
    if (!history.length) return "";

    const chips = history
      .map((code) => {
        const name = getStopName(code);
        return `
        <div class="search-chip" data-chip-stop="${code}" title="Filtra per ${escapeHtml(name)}">
          <span>${escapeHtml(name)}</span>
          <button type="button" class="search-chip-delete" data-chip-delete="${code}" aria-label="Elimina dalla cronologia">✕</button>
        </div>
      `;
      })
      .join("");

    return `
      <div class="search-chips-container">
        <div class="search-chips-title">Ricerche recenti</div>
        <div class="search-chips">${chips}</div>
      </div>
    `;
  } catch (e) {
    return "";
  }
}

function renderFilterBar(lineConfig, cfg, stopFilter, lineFilter, showAllLines, followedLineOrder) {
  const cityLineOrder = followedLineOrder || cfg.lineOrder || Object.keys(lineConfig);
  const allLineIds = Object.keys(lineConfig).sort();
  const lineOrder = showAllLines ? allLineIds : cityLineOrder;
  const inputValue = stopFilter ? getStopName(stopFilter) : "";

  // Build line pills (scrollable for many lines)
  const linePills = [
    `<button type="button" data-line-filter="" class="${!lineFilter ? "active" : ""}">Tutte <small>(${lineOrder.length})</small></button>`,
    ...lineOrder
      .filter((id) => lineConfig[id]) // only show lines that have config
      .map((id) => {
        const isOutsideCity = !cityLineOrder.includes(id);
        return `<button type="button" data-line-filter="${id}" class="${lineFilter === id ? "active" : ""}${isOutsideCity ? " line-outside" : ""}" title="${lineConfig[id]?.label || id}">${id}</button>`;
      })
  ].join("");

  // Toggle "Show all lines"
  const arrowSvg = showAllLines
    ? `<svg viewBox="0 0 24 24" width="14" height="14" stroke="currentColor" stroke-width="2.5" fill="none" stroke-linecap="round" stroke-linejoin="round" style="vertical-align: middle; margin-right: 2px;"><line x1="19" y1="12" x2="5" y2="12"></line><polyline points="12 19 5 12 12 5"></polyline></svg>`
    : `<svg viewBox="0 0 24 24" width="14" height="14" stroke="currentColor" stroke-width="2.5" fill="none" stroke-linecap="round" stroke-linejoin="round" style="vertical-align: middle; margin-left: 2px;"><line x1="5" y1="12" x2="19" y2="12"></line><polyline points="12 5 19 12 12 19"></polyline></svg>`;

  const state = lastArgs.state;
  const focusCity = state.settings?.focusCity || cfg.defaults?.focusCity || "BT";
  const followedForCity = state.settings?.followedLinesByCity?.[focusCity];
  const userActiveLines = state.settings?.userProfile?.activeLines;
  const hasCustomLines =
    Array.isArray(followedForCity) ||
    (Array.isArray(userActiveLines) &&
      userActiveLines.length > 0 &&
      !state.settings?.userProfile?.skipped &&
      focusCity === (state.settings?.userProfile?.focusCity || "BT"));

  const cityOrCustomLabel = hasCustomLines
    ? "linee personalizzate"
    : cfg.activeCityConfig?.name || "la mia città";

  const allLinesToggle = `
    <button type="button" class="text-btn line-scope-toggle" data-toggle-all-lines>
      ${showAllLines ? `${arrowSvg} Solo ${cityOrCustomLabel} (${cityLineOrder.length})` : `Mostra tutte le linee (${allLineIds.length}) ${arrowSvg}`}
    </button>`;

  // Active filter indicator
  let activeInfo = "";
  if (stopFilter || lineFilter) {
    const parts = [];
    if (stopFilter) {
      parts.push(
        `<span class="active-filter-badge"><svg viewBox="0 0 24 24" width="12" height="12" stroke="currentColor" stroke-width="2.5" fill="none" stroke-linecap="round" stroke-linejoin="round" style="vertical-align: -1px; margin-right: 4px;"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path><circle cx="12" cy="10" r="3"></circle></svg> <strong title="${escapeHtml(getStopName(stopFilter))} [${stopFilter}]">${escapeHtml(getStopName(stopFilter))}</strong></span>`
      );
    }
    if (lineFilter) {
      parts.push(
        `<span class="active-filter-badge"><svg viewBox="0 0 24 24" width="12" height="12" stroke="currentColor" stroke-width="2.5" fill="none" stroke-linecap="round" stroke-linejoin="round" style="vertical-align: -1px; margin-right: 4px;"><rect x="2" y="3" width="20" height="14" rx="2" ry="2"></rect><line x1="6" y1="21" x2="6" y2="17"></line><line x1="18" y1="21" x2="18" y2="17"></line><line x1="2" y1="10" x2="22" y2="10"></line></svg> <strong>${lineFilter}</strong></span>`
      );
    }
    activeInfo = `<div class="filter-active-info">
      <div style="display: flex; flex-wrap: wrap; gap: 6px; align-items: center;">${parts.join("")}</div>
      <button type="button" class="text-btn" data-clear-filters>Azzera filtri</button>
    </div>`;
  }

  const fermataSvg = `<svg viewBox="0 0 24 24" width="14" height="14" stroke="currentColor" stroke-width="2.5" fill="none" stroke-linecap="round" stroke-linejoin="round" style="vertical-align: -2px; margin-right: 4px;"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path><circle cx="12" cy="10" r="3"></circle></svg>`;
  const busSvg = `<svg viewBox="0 0 24 24" width="14" height="14" stroke="currentColor" stroke-width="2.5" fill="none" stroke-linecap="round" stroke-linejoin="round" style="vertical-align: -2px; margin-right: 4px;"><rect x="2" y="3" width="20" height="14" rx="2" ry="2"></rect><line x1="6" y1="21" x2="6" y2="17"></line><line x1="18" y1="21" x2="18" y2="17"></line><line x1="2" y1="10" x2="22" y2="10"></line></svg>`;

  const chipsHtml = getSearchHistoryChipsHTML();

  const isFiltered = Boolean(stopFilter || lineFilter);
  const activeSummaryBadge = isFiltered
    ? `<span class="badge" style="font-size: 0.72rem; background: rgba(34,211,238,0.15); color: var(--accent); border: 1px solid rgba(34,211,238,0.3); padding: 2px 8px; border-radius: 6px;">Filtro attivo</span>`
    : `<span style="font-size: 0.75rem; color: var(--muted); font-weight: 500;">Tocca per aprire filtri ▾</span>`;

  return `
  <details class="live-filter-accordion" ${isFiltered ? "open" : ""}>
    <summary>
      <span style="display: inline-flex; align-items: center; gap: 6px;">🔍 <span>Cerca o filtra fermata / linea</span></span>
      ${activeSummaryBadge}
    </summary>
    <section class="filter-bar">
      <div class="filter-group">
        <span class="filter-label">${fermataSvg} Fermata</span>
        <div class="stop-search-wrapper">
          <svg class="search-input-icon" viewBox="0 0 24 24" width="18" height="18" stroke="currentColor" stroke-width="2.5" fill="none" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>
          <input type="text" class="stop-search-input" data-stop-search
            placeholder="Cerca fermata..." autocomplete="off"
            value="${escapeHtml(inputValue)}">
          ${stopFilter ? '<button type="button" class="stop-search-clear" data-stop-search-clear>✕</button>' : ""}
          <div class="stop-search-results" data-stop-results></div>
        </div>
        <div id="search-chips-wrapper">${chipsHtml}</div>
      </div>
      <div class="filter-group">
        <span class="filter-label">${busSvg} Linea</span>
        <div class="filter-pills filter-pills-scroll">${linePills}</div>
      </div>
      ${allLinesToggle}
      ${activeInfo}
    </section>
  </details>`;
}

function buildSearchResults(query) {
  if (!query || query.length < 1) return "";
  const q = query.toLowerCase();
  let html = "";
  for (const { prefix, label } of FILTER_CITY_ORDER) {
    const codes = Object.keys(STOP_NAMES)
      .filter(
        (c) =>
          c.startsWith(prefix) &&
          (STOP_NAMES[c].toLowerCase().includes(q) ||
            getStopName(c).toLowerCase().includes(q) ||
            c.toLowerCase().includes(q))
      )
      .sort();
    if (!codes.length) continue;
    html += `<div class="search-group-label">${escapeHtml(label)}</div>`;
    for (const code of codes) {
      html += `<button type="button" class="search-result-item" data-stop-pick="${code}">${escapeHtml(getStopName(code))}</button>`;
    }
  }
  return html || `<div class="search-no-results">Nessuna fermata trovata</div>`;
}

// getUserFavorite, getProfile, compareCardsByDeparture moved to live-logic.js

// buildOutboundCard, buildReturnCard, getConfiguredStops, chooseArrivalAfter moved to live-logic.js
// PIN_SVG, EDIT_SVG, CALENDAR_SVG and all card render functions moved to live-cards.js
